/**
 * DSonic Strudel Integration Module
 *
 * Shared code for integrating DSonic with Strudel REPL editors.
 * Used by both the overview page and engines page.
 */

import { registerSound, getAudioContext } from '@strudel/webaudio';
import { createParams as importedCreateParams } from '@strudel/core';
import * as DSonic from 'dsonic';

// Export DSonic and Strudel globals for window access
export { DSonic, registerSound, getAudioContext };

/**
 * Wait for Strudel's global functions to be available.
 * Strudel may take some time to initialize and expose its globals.
 *
 * @returns {Promise<{createParams: Function, registerSound: Function}>}
 */
export async function waitForStrudelGlobals() {
  for (let i = 0; i < 30; i++) {
    if (typeof globalThis.createParams === 'function') {
      console.log('Using Strudel globalThis.createParams');
      return {
        createParams: globalThis.createParams,
        registerSound: globalThis.registerSound || registerSound
      };
    }
    await new Promise(r => setTimeout(r, 100));
  }
  console.log('Using imported createParams and registerSound');
  return {
    createParams: importedCreateParams,
    registerSound: registerSound
  };
}

/**
 * Engine configuration array - defines all 13 DSonic engine types
 * with their templates and default settings.
 */
export const ENGINE_CONFIGS = [
  { name: 'akick', type: 'analog_kick', templates: DSonic.ANALOG_KICK_TEMPLATES, default: 'house' },
  { name: 'membrane', type: 'membrane', templates: DSonic.MEMBRANE_TEMPLATES, default: 'tightSnare' },
  { name: 'hsnare', type: 'hybrid_snare', templates: DSonic.HYBRID_SNARE_TEMPLATES, default: 'analog' },
  { name: 'hclap', type: 'clap', templates: DSonic.CLAP_TEMPLATES, default: 'classic' },
  { name: 'hhat', type: 'hat', templates: DSonic.HAT_TEMPLATES, default: 'closed', baseParams: { mode: 'closed' } },
  { name: 'ohat', type: 'hat', templates: DSonic.HAT_TEMPLATES, default: 'open', baseParams: { mode: 'open' } },
  { name: 'hcymbal', type: 'cymbal', templates: DSonic.CYMBAL_TEMPLATES, default: 'crash' },
  { name: 'htom', type: 'tom', templates: DSonic.TOM_TEMPLATES, default: 'floorTom' },
  { name: 'hrim', type: 'rim', templates: DSonic.RIM_TEMPLATES, default: 'rimshot' },
  { name: 'shaker', type: 'shaker', templates: DSonic.SHAKER_TEMPLATES, default: 'shaker' },
  { name: 'fold', type: 'fold', templates: DSonic.FOLD_TEMPLATES, default: 'foldKick' },
  { name: 'impact', type: 'impact', templates: DSonic.IMPACT_TEMPLATES, default: 'brush' },
  { name: 'fmperc', type: 'fm', templates: DSonic.FM_TEMPLATES, default: 'fmKick' }
];

/**
 * All DSonic control parameters that can be used in Strudel patterns.
 * These are registered with Strudel's createParams to enable pattern control.
 */
export const DSONIC_PARAM_NAMES = [
  'pitch', 'type', 'algorithm', 'drive', 'mode', 'tune', 'cutoff', 'decay',
  'penv', 'pdecay', 'nfreq', 'nres', 'ntype', 'nlevel', 'nfilter',
  'fold', 'crush',
  'wgsend', 'wgtune', 'wgdecay', 'wgbody', 'wgmode',
  'bodyPitch', 'bodyDecay', 'pitchEnvAmt', 'pitchEnvDecay',
  'clickLevel', 'clickTone', 'driveAmt', 'driveType', 'lowShelfGain',
  'pitch1', 'pitch2', 'coupling12', 'wireLevel', 'wireTension', 'snareWireTension',
  'strikeType', 'strikeHardness',
  'bumpAmt', 'bumpPitch', 'noiseLevel', 'noiseSnap', 'ringAmt', 'ringSpread', 'snapAmt',
  'bodyResAmt', 'filterEnvAmt', 'transientLevel', 'transientFreq', 'noiseColor',
  'noiseResonance', 'toneAmt', 'tonePitch', 'earlyReflections', 'roomSize', 'noiseWidth',
  'oscTune', 'hiFilterFreq', 'loFilterFreq', 'bandBalance',
  'metalAmt', 'metalRichness', 'inharmonicity', 'srrAmt', 'sizzleAmt', 'gritTone',
  'richness', 'clusterDensity', 'metalCharacter', 'topAmt', 'size',
  'fmAmt', 'fmModFreq', 'fmCarrierFreq', 'toneTilt',
  'topTuning', 'edgePeakAmt', 'feedbackAmt',
  'pingAmt', 'pingFreq', 'pingDecay',
  'tailHpf', 'tailOpenTime', 'spectralSlope',
  'hpfCutoff', 'lpfCutoff', 'filterRes',
  'tension', 'overtoneAmt',
  'dualStrike', 'enhance', 'snareTimbreAmt',
  'rattleType', 'grainSize', 'grainDensity', 'hardness', 'tone',
  'foldAmt', 'foldSymmetry', 'tablePosition', 'tableWarp', 'filterRes',
  'transientType', 'transientPitch', 'transientBrightness', 'transientLevel', 'transientLength',
  'transType', 'transBright', 'transLevel',
  'bodyType', 'bodyFilterCutoff', 'bodyFilterRes', 'bodyLevel',
  'tailType', 'tailDecay', 'tailColor', 'tailLevel', 'layerMix', 'overallDecay',
  'modIndex', 'modIndexDecay', 'brightness', 'inharmonicityBias'
];

/**
 * Creates a trigger function for a specific engine type.
 * This function is registered with Strudel's registerSound.
 *
 * @param {string} engineType - The DSonic engine type (e.g., 'analog_kick')
 * @param {Object} templates - Template presets for this engine
 * @param {string} defaultTemplate - Default template name to use
 * @param {Object} [baseParams] - Optional base parameters merged into all sounds
 * @returns {Function} Trigger function for Strudel
 */
export function createEngineTrigger(engineType, templates, defaultTemplate, baseParams) {
  return function(time, value, onended) {
    try {
      const ctx = getAudioContext();
      const templateName = value.template || value.t || defaultTemplate;
      const templateParams = templates[templateName] || templates[defaultTemplate] || {};

      const patternParams = {};
      for (const key in value) {
        if (key.startsWith('_') || key === 's' || key === 'n' || key === 'template' || key === 't') continue;
        patternParams[key] = value[key];
      }

      const mergedParams = { ...(baseParams || {}), ...templateParams, ...patternParams };
      const engine = DSonic.createEngine(ctx, engineType, mergedParams);
      const result = engine.trigger(time);

      // Connect engine output directly to destination
      const outputNode = engine.getOutput();
      outputNode.connect(ctx.destination);

      // Create a silent node for Strudel's routing (it expects a valid AudioNode)
      // This prevents context mismatch errors while we handle audio ourselves
      const silentNode = ctx.createGain();
      silentNode.gain.value = 0;

      return {
        node: silentNode,
        stop: function(stopTime) {
          result.stop(stopTime);
          setTimeout(function() {
            try {
              outputNode.disconnect();
              silentNode.disconnect();
            } catch (e) { /* ignore */ }
            engine.dispose();
            onended();
          }, 500);
        }
      };
    } catch (err) {
      console.error('[DSonic] Engine trigger error:', err);
      // Return valid silent node on error to prevent Strudel from hanging
      const ctx = getAudioContext();
      const errorNode = ctx.createGain();
      errorNode.gain.value = 0;
      onended();
      return {
        node: errorNode,
        stop: function() { onended(); }
      };
    }
  };
}

/**
 * Register all DSonic engine sounds with Strudel.
 * Each engine gets a base sound (dsonic_<name>) plus template variants.
 *
 * @param {Object} strudelGlobals - Object with createParams and registerSound
 * @param {Object} [options] - Options for registration
 * @param {number} [options.maxTemplates] - Max templates per engine (default: all)
 */
export function registerEngineSounds(strudelGlobals, options = {}) {
  if (!strudelGlobals) return;
  const strudelRegisterSound = strudelGlobals.registerSound;
  const maxTemplates = options.maxTemplates;

  ENGINE_CONFIGS.forEach(function(config) {
    // Register base engine sound
    strudelRegisterSound(
      'dsonic_' + config.name,
      createEngineTrigger(config.type, config.templates, config.default, config.baseParams),
      { type: 'synth', tags: ['drum', config.name, 'dsonic'] }
    );

    // Register template variants
    const templateNames = Object.keys(config.templates);
    const templatesToRegister = maxTemplates ? templateNames.slice(0, maxTemplates) : templateNames;

    templatesToRegister.forEach(function(templateName) {
      strudelRegisterSound(
        'dsonic_' + config.name + '_' + templateName,
        createEngineTrigger(config.type, config.templates, templateName, config.baseParams),
        { type: 'synth', tags: ['drum', config.name, templateName, 'dsonic'] }
      );
    });
  });

  console.log('DSonic engine sounds registered');
}

/**
 * Register DSonic control parameters with Strudel.
 * This allows using methods like .bodyPitch(), .decay() in patterns.
 *
 * @returns {Promise<Object|null>} Strudel globals object or null if unavailable
 */
export async function registerDSonicControls() {
  const strudelGlobals = await waitForStrudelGlobals();
  if (!strudelGlobals) {
    console.error('Cannot register DSonic - Strudel globals not available');
    return null;
  }

  const dsonicParams = strudelGlobals.createParams(...DSONIC_PARAM_NAMES);
  Object.assign(window, dsonicParams);
  window.createParams = strudelGlobals.createParams;

  console.log('DSonic controls registered with Strudel Pattern class');
  return strudelGlobals;
}

/**
 * Register template-based DSonic sounds (legacy voice system).
 * Used by the overview page for template aliases like dsonic_kick, dsonic_snare.
 *
 * @param {Object} strudelGlobals - Object with createParams and registerSound
 * @param {Object} drumSynth - Initialized DrumSynth instance
 */
export function registerTemplateSounds(strudelGlobals, drumSynth) {
  if (!strudelGlobals) return;
  const strudelRegisterSound = strudelGlobals.registerSound;
  const { getTemplateByAlias, mapStrudelParams } = DSonic;

  const templates = ['kick', 'snare', 'hihat', 'openhat', 'clap', 'tom', 'ride', 'crash', 'rimshot', 'noise'];

  templates.forEach(function(templateName) {
    strudelRegisterSound(
      'dsonic_' + templateName,
      function(time, value, onended) {
        const template = getTemplateByAlias(templateName);
        const baseParams = template ? template.params : undefined;
        const params = mapStrudelParams(value);
        const result = drumSynth.trigger(time, params, baseParams);
        return {
          node: result.node,
          stop: function(stopTime) {
            result.stop(stopTime);
            setTimeout(onended, 200);
          }
        };
      },
      { type: 'synth', tags: ['drum', templateName] }
    );
  });

  // Register generic dsonic sound
  strudelRegisterSound(
    'dsonic',
    function(time, value, onended) {
      const templateName = value.template || value.preset || value.t;
      const template = templateName ? getTemplateByAlias(templateName) : undefined;
      const baseParams = template ? template.params : undefined;
      const params = mapStrudelParams(value);
      const result = drumSynth.trigger(time, params, baseParams);
      return {
        node: result.node,
        stop: function(stopTime) {
          result.stop(stopTime);
          setTimeout(onended, 200);
        }
      };
    },
    { type: 'synth', tags: ['drum', 'synthesizer'] }
  );

  console.log('DSonic template sounds registered');
}

/**
 * Initialize DSonic with Strudel integration.
 * Sets up global variables and returns initialized drumSynth.
 *
 * @returns {Object} Initialized DrumSynth instance
 */
export function initializeDSonic() {
  window.DSonic = DSonic;
  window.registerSound = registerSound;
  window.getAudioContext = getAudioContext;
  window.isInitialized = false;

  const audioContext = getAudioContext();
  const drumSynth = DSonic.initDSonic(audioContext);

  return drumSynth;
}

/**
 * Full initialization for pages with interactive Strudel editors.
 * Registers both controls and sounds, sets isInitialized flag.
 *
 * @param {Object} [options] - Initialization options
 * @param {boolean} [options.registerTemplates=true] - Register template sounds
 * @param {boolean} [options.registerEngines=true] - Register engine sounds
 * @param {number} [options.maxTemplates] - Max templates per engine
 * @returns {Promise<{drumSynth: Object, strudelGlobals: Object}>}
 */
export async function fullInitialize(options = {}) {
  const { registerTemplates = true, registerEngines = true, maxTemplates } = options;

  const drumSynth = initializeDSonic();
  const strudelGlobals = await registerDSonicControls();

  if (!strudelGlobals) {
    console.error('DSonic initialization failed - Strudel globals not available');
    window.isInitialized = true;
    return { drumSynth, strudelGlobals: null };
  }

  if (registerTemplates) {
    registerTemplateSounds(strudelGlobals, drumSynth);
  }

  if (registerEngines) {
    registerEngineSounds(strudelGlobals, { maxTemplates });
  }

  window.isInitialized = true;
  console.log('DSonic + Strudel initialized successfully!');

  return { drumSynth, strudelGlobals };
}
